import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Info, AlertTriangle, AlertOctagon, CheckCircle2, X } from 'lucide-react';

const ICONS = { info: Info, warning: AlertTriangle, critical: AlertOctagon, success: CheckCircle2 };
// Severity reads three ways at once: the stripe, the icon color, and the icon
// glyph. Tinted grounds keep body copy on --foreground, which stays legible in
// both themes where a saturated fill would not.
const STYLES = {
  info:     { bar: 'border-l-primary bg-accent', text: 'text-primary' },
  warning:  { bar: 'border-l-[hsl(var(--warning))] bg-[hsl(var(--warning)/0.12)]', text: 'text-[hsl(var(--warning))]' },
  critical: { bar: 'border-l-destructive bg-destructive/12', text: 'text-destructive' },
  success:  { bar: 'border-l-[hsl(var(--success))] bg-[hsl(var(--success)/0.12)]', text: 'text-[hsl(var(--success))]' },
};

const DISMISS_KEY = 'announcements_dismissed_v1';

const getDismissed = () => {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}'); } catch { return {}; }
};
const dismiss = (id, version) => {
  const d = getDismissed();
  d[id] = version || 1;
  localStorage.setItem(DISMISS_KEY, JSON.stringify(d));
};

export default function AnnouncementsHost() {
  const [items, setItems] = useState([]);
  const [popupItem, setPopupItem] = useState(null);
  const [dismissedMap, setDismissedMap] = useState(getDismissed());

  const load = useCallback(async () => {
    try {
      const r = await api.get('/announcements/active');
      const all = r.data?.announcements || [];
      setItems(all);
      // Find first non-dismissed popup
      const dis = getDismissed();
      const nextPopup = all.find(a => a.style === 'popup' && dis[a.id] !== (a.version || 1));
      setPopupItem(nextPopup || null);
    } catch { /* user might be logged out, ignore */ }
  }, []);

  useEffect(() => {
    load();
    // Re-poll every 5 minutes so members get fresh updates without refresh
    const t = setInterval(load, 5 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [load]);

  const onDismiss = (a) => {
    dismiss(a.id, a.version || 1);
    setDismissedMap(getDismissed());
    if (popupItem?.id === a.id) setPopupItem(null);
  };

  const banners = items.filter(a => a.style === 'banner' && dismissedMap[a.id] !== (a.version || 1));

  return (
    <>
      {/* Stacked banner bar at top of every page when logged in */}
      {banners.length > 0 && (
        <div className="sticky top-0 z-50 space-y-1" data-testid="announcement-banner-stack">
          {banners.map(a => {
            const Icon = ICONS[a.severity] || Info;
            const s = STYLES[a.severity] || STYLES.info;
            return (
              <div key={a.id} className={`border-y border-l-[3px] border-border px-4 py-2.5 ${s.bar} flex items-center gap-3 text-foreground`} data-testid={`announcement-banner-${a.id}`}>
                <Icon className={`h-4 w-4 flex-shrink-0 ${s.text}`} />
                <div className="flex-1 min-w-0 text-sm">
                  <span className="font-semibold">{a.title}</span>
                  <span className="text-muted-foreground ml-2">{a.message}</span>
                </div>
                {a.dismissible && (
                  <button
                    type="button"
                    onClick={() => onDismiss(a)}
                    className="opacity-80 hover:opacity-100 flex-shrink-0 min-h-[44px] min-w-[44px] inline-flex items-center justify-center -mr-2"
                    aria-label="Dismiss"
                    data-testid={`announcement-dismiss-${a.id}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Popup modal */}
      <Dialog open={!!popupItem} onOpenChange={(o) => { if (!o && popupItem) onDismiss(popupItem); }}>
        <DialogContent className="bg-card max-w-md" data-testid="announcement-popup">
          {popupItem && (() => {
            const Icon = ICONS[popupItem.severity] || Info;
            const s = STYLES[popupItem.severity] || STYLES.info;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className={`font-display flex items-center gap-2 ${s.text}`}>
                    <Icon className="h-5 w-5" /> {popupItem.title}
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{popupItem.message}</p>
                <DialogFooter>
                  <Button onClick={() => onDismiss(popupItem)} className="btn-hover" data-testid="announcement-popup-dismiss">Got it</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
}
